import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Services from "@/pages/Services";
import Packages from "@/pages/Packages";
import PackagesDetails from "@/pages/PackagesDetails";
import VisaSponsorship from "@/pages/VisaSponsorship";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";

import SupplierProducts from "@/pages/supplier/SupplierProducts";
import SupplierProfile from "@/pages/supplier/SupplierProfile";
import ProductForm from "@/pages/supplier/ProductForm";
import SupplierProductDetail from "@/pages/supplier/SupplierProductDetail";
import SupplierRFQBoard from "@/pages/supplier/SupplierRFQBoard";

import Catalog from "@/pages/buyer/Catalog";
import BuyerProduct from "@/pages/buyer/BuyerProduct";
import BuyerQuotes from "@/pages/buyer/BuyerQuotes";
import BuyerInquiries from "@/pages/buyer/BuyerInquiries";
import BuyerShipments from "@/pages/buyer/BuyerShipments";

import SupplierReviews from "@/pages/supplier/SupplierReviews";
import SupplierDirectory from "@/pages/SupplierDirectory";
import SupplierStorefront from "@/pages/SupplierStorefront";
import SupplierProductPublic from "@/pages/SupplierProductPublic";

import AdminUsers from "@/pages/admin/AdminUsers";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminProductReview from "@/pages/admin/AdminProductReview";
import AdminQuotes from "@/pages/admin/AdminQuotes";
import AdminInquiries from "@/pages/admin/AdminInquiries";
import AdminShipments from "@/pages/admin/AdminShipments";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminMetrics from "@/pages/admin/AdminMetrics";

import Messages from "@/pages/Messages";
import Profile from "@/pages/Profile";
import Notifications from "@/pages/Notifications";
import Escrows from "@/pages/Escrows";
import SampleOrders from "@/pages/SampleOrders";

function AppRoutes() {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Keep the browser tab title in sync with the current i18n language
  useEffect(() => {
    const update = () => {
      const title = t("meta.title");
      const desc = t("meta.description");
      if (title && document.title !== title) document.title = title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc && desc) metaDesc.setAttribute("content", desc);
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle && title) ogTitle.setAttribute("content", title);
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc && desc) ogDesc.setAttribute("content", desc);
      document.documentElement.lang = i18n.language || "en";
    };
    update();
    i18n.on("languageChanged", update);
    return () => i18n.off("languageChanged", update);
  }, [t, i18n]);

  // Detect Google OAuth session_id in URL fragment (must be synchronous during render)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/services" element={<Services />} />
      <Route path="/packages" element={<Packages />} />
      <Route path="/packages/details" element={<PackagesDetails />} />
      <Route path="/services/visa" element={<VisaSponsorship />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/suppliers" element={<SupplierDirectory />} />
      <Route path="/suppliers/:sid" element={<SupplierStorefront />} />
      <Route path="/products/:pid" element={<SupplierProductPublic />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/messages/:threadId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />

      {/* Supplier */}
      <Route path="/supplier/profile" element={<ProtectedRoute roles={["supplier"]}><SupplierProfile /></ProtectedRoute>} />
      <Route path="/supplier/products" element={<ProtectedRoute roles={["supplier"]}><SupplierProducts /></ProtectedRoute>} />
      <Route path="/supplier/products/new" element={<ProtectedRoute roles={["supplier"]}><ProductForm /></ProtectedRoute>} />
      <Route path="/supplier/products/:pid/edit" element={<ProtectedRoute roles={["supplier"]}><ProductForm /></ProtectedRoute>} />
      <Route path="/supplier/products/:pid" element={<ProtectedRoute roles={["supplier"]}><SupplierProductDetail /></ProtectedRoute>} />
      <Route path="/supplier/reviews" element={<ProtectedRoute roles={["supplier"]}><SupplierReviews /></ProtectedRoute>} />
      <Route path="/supplier/rfq" element={<ProtectedRoute roles={["supplier"]}><SupplierRFQBoard /></ProtectedRoute>} />

      {/* Buyer */}
      <Route path="/buyer/catalog" element={<ProtectedRoute roles={["buyer","admin"]}><Catalog /></ProtectedRoute>} />
      <Route path="/buyer/products/:pid" element={<ProtectedRoute roles={["buyer","admin"]}><BuyerProduct /></ProtectedRoute>} />
      <Route path="/buyer/quotes" element={<ProtectedRoute roles={["buyer"]}><BuyerQuotes /></ProtectedRoute>} />
      <Route path="/buyer/inquiries" element={<ProtectedRoute roles={["buyer"]}><BuyerInquiries /></ProtectedRoute>} />
      <Route path="/buyer/shipments" element={<ProtectedRoute roles={["buyer","supplier"]}><BuyerShipments /></ProtectedRoute>} />
      <Route path="/buyer/samples" element={<ProtectedRoute><SampleOrders /></ProtectedRoute>} />
      <Route path="/escrows" element={<ProtectedRoute><Escrows /></ProtectedRoute>} />

      {/* Admin */}
      <Route path="/admin/users" element={<ProtectedRoute roles={["admin"]}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/products" element={<ProtectedRoute roles={["admin"]}><AdminProducts /></ProtectedRoute>} />
      <Route path="/admin/products/:pid" element={<ProtectedRoute roles={["admin"]}><AdminProductReview /></ProtectedRoute>} />
      <Route path="/admin/quotes" element={<ProtectedRoute roles={["admin"]}><AdminQuotes /></ProtectedRoute>} />
      <Route path="/admin/inquiries" element={<ProtectedRoute roles={["admin"]}><AdminInquiries /></ProtectedRoute>} />
      <Route path="/admin/shipments" element={<ProtectedRoute roles={["admin"]}><AdminShipments /></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute roles={["admin"]}><AdminLeads /></ProtectedRoute>} />
      <Route path="/admin/metrics" element={<ProtectedRoute roles={["admin"]}><AdminMetrics /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
